import { useMemo } from "react";
import PropTypes from "prop-types";
import Chart from "react-apexcharts";
import Icon from "@mui/material/Icon";
import Grid from "@mui/material/Grid";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { useICCCColors, ICCC_COLORS as ICCC_FALLBACK } from "./theme";
import { categoryMeta } from "../model/constants";

const ringColorFor = (unhealthyPct) =>
  unhealthyPct >= 40 ? ICCC_FALLBACK.critical : unhealthyPct >= 20 ? ICCC_FALLBACK.warning : ICCC_FALLBACK.healthy;

const GroupTile = ({ group, isSelected, onSelect, colors }) => {
  const ICCC_COLORS = colors;
  const unhealthy = group.critical + group.warning + group.unknown;
  const unhealthyPct = group.total ? Math.round((unhealthy / group.total) * 100) : 0;
  const ringColor = ringColorFor(unhealthyPct);
  const meta = categoryMeta(group.code);

  const options = useMemo(
    () => ({
      chart: { sparkline: { enabled: true }, type: "radialBar", foreColor: ICCC_COLORS.textSecondary },
      plotOptions: {
        radialBar: {
          hollow: { size: "58%", background: "transparent" },
          track: { background: ICCC_COLORS.hoverBg, strokeWidth: "100%" },
          dataLabels: {
            name: { show: false },
            value: {
              offsetY: 6,
              fontSize: "18px",
              fontWeight: 700,
              color: ICCC_COLORS.textPrimary,
              formatter: (v) => `${v}%`,
            },
          },
        },
      },
      fill: { colors: [ringColor] },
      stroke: { lineCap: "round" },
    }),
    [ringColor, ICCC_COLORS]
  );

  return (
    <MDBox
      role="button"
      tabIndex={0}
      aria-pressed={isSelected}
      aria-label={`${meta.label}: ${group.total} devices, ${unhealthyPct}% unhealthy`}
      onClick={() => onSelect?.(group)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onSelect?.(group);
        }
      }}
      sx={{
        cursor: "pointer",
        textAlign: "center",
        p: 1,
        borderRadius: 1,
        border: `1px solid ${isSelected ? ringColor : "transparent"}`,
        bgcolor: isSelected ? `${ringColor}1f` : "transparent",
        transition: "all 120ms ease",
        outline: "none",
        "&:hover": { bgcolor: ICCC_COLORS.hoverBg },
        "&:focus-visible": { border: `1px solid ${ICCC_COLORS.accent}` },
      }}
    >
      <Icon sx={{ fontSize: 30, color: ICCC_COLORS.textSecondary }}>{meta.icon}</Icon>
      <MDTypography
        variant="caption"
        fontWeight="bold"
        sx={{ display: "block", color: ICCC_COLORS.textPrimary, fontSize: 12, mt: 0.25 }}
      >
        {meta.label}
      </MDTypography>
      <MDTypography variant="caption" sx={{ display: "block", color: ICCC_COLORS.textMuted, fontSize: 10, mb: 0.25 }}>
        {group.total.toLocaleString("en-IN")} Devices
      </MDTypography>
      <Chart options={options} series={[unhealthyPct]} type="radialBar" height={100} />
      <MDBox sx={{ fontSize: 11, mt: 0.25, fontWeight: 700, letterSpacing: 0.3 }}>
        <span style={{ color: ICCC_COLORS.healthy }}>{group.healthy}</span>{" "}
        <span style={{ color: ICCC_COLORS.warning }}>{group.warning}</span>{" "}
        <span style={{ color: ICCC_COLORS.critical }}>{group.critical}</span>{" "}
        <span style={{ color: ICCC_COLORS.unknown }}>{group.unknown}</span>
      </MDBox>
    </MDBox>
  );
};

GroupTile.propTypes = {
  group: PropTypes.shape({
    code: PropTypes.string,
    total: PropTypes.number,
    healthy: PropTypes.number,
    warning: PropTypes.number,
    critical: PropTypes.number,
    unknown: PropTypes.number,
  }).isRequired,
  isSelected: PropTypes.bool,
  onSelect: PropTypes.func,
  colors: PropTypes.object,
};

const LEGEND = [
  { c: ICCC_FALLBACK.healthy, l: "Healthy" },
  { c: ICCC_FALLBACK.warning, l: "Warning" },
  { c: ICCC_FALLBACK.critical, l: "Critical" },
  { c: ICCC_FALLBACK.unknown, l: "Unknown" },
];

export default function GroupwiseHealth({ groups = [], onSelectGroup, selectedGroup }) {
  const ICCC_COLORS = useICCCColors();
  const visible = groups.filter((g) => g.total > 0);

  return (
    <MDBox
      sx={{
        bgcolor: ICCC_COLORS.cardBg,
        border: `1px solid ${ICCC_COLORS.cardBorder}`,
        borderRadius: 1.5,
        p: 2,
        height: "100%",
        boxShadow: ICCC_COLORS.shadow,
      }}
    >
      <MDTypography variant="h6" fontWeight="bold" sx={{ mb: 1.5, color: ICCC_COLORS.textPrimary }}>
        Group-wise Device Health
      </MDTypography>

      {visible.length === 0 ? (
        <MDBox sx={{ height: 160, display: "flex", alignItems: "center", justifyContent: "center", color: ICCC_COLORS.textMuted }}>
          No device groups to display
        </MDBox>
      ) : (
        <Grid container spacing={1}>
          {visible.map((g) => (
            <Grid item xs={6} sm={4} md={2} key={g.code}>
              <GroupTile group={g} isSelected={selectedGroup === g.code} onSelect={onSelectGroup} colors={ICCC_COLORS} />
            </Grid>
          ))}
        </Grid>
      )}

      <MDBox
        display="flex"
        gap={2}
        justifyContent="center"
        sx={{ mt: 1, pt: 1, borderTop: `1px dashed ${ICCC_COLORS.divider}` }}
      >
        {LEGEND.map((x) => (
          <MDBox key={x.l} display="flex" alignItems="center" gap={0.5}>
            <MDBox sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: x.c }} />
            <MDTypography variant="caption" sx={{ color: ICCC_COLORS.textSecondary, fontSize: 11 }}>
              {x.l}
            </MDTypography>
          </MDBox>
        ))}
      </MDBox>
    </MDBox>
  );
}

GroupwiseHealth.propTypes = {
  groups: PropTypes.arrayOf(PropTypes.object),
  onSelectGroup: PropTypes.func,
  selectedGroup: PropTypes.string,
};
